/**
 * Sign-in with Google and Microsoft (Supabase providers `google` and `azure`).
 * Pure, so vitest covers it (src/lib/auth/__tests__/oauth.test.ts).
 *
 * The provider returns to /auth on the host it was started from, the same
 * place the e-mail confirmation link returns to, so `next` (the MCP consent
 * on zettlapp.com) and `plan` keep working; the Auth page then exchanges the
 * PKCE code and sends the person on (afterAuthPath).
 */
import type { PlanId } from "@/lib/pricing/plans";
import { termsAcceptance, type TermsAcceptance } from "@/lib/legal/terms/version";
import { signupRedirectUrl, type SignupLanguage } from "./signup";

export type OAuthProvider = "google" | "azure";

export const OAUTH_PROVIDERS: readonly OAuthProvider[] = ["google", "azure"];

export interface OAuthRequest {
  provider: OAuthProvider;
  options: { redirectTo: string; scopes?: string };
}

/** The `signInWithOAuth` call for a provider, started on `origin`. */
export function oauthRequest(
  provider: OAuthProvider,
  input: { origin: string; language: SignupLanguage; plan: PlanId | null; next: string | null }
): OAuthRequest {
  const redirectTo = signupRedirectUrl(input.origin, input.language, input.plan, input.next);
  // Supabase needs Azure to return an e-mail address, so `email` is requested explicitly.
  return provider === "azure"
    ? { provider, options: { redirectTo, scopes: "email" } }
    : { provider, options: { redirectTo } };
}

/**
 * Terms accepted on the register tab before leaving for Google or Microsoft.
 * `signInWithOAuth` cannot carry user metadata, so the acceptance waits in
 * this browser and is written to the account when it comes back.
 */
export const PENDING_TERMS_KEY = "zettl:pending-terms";
/** An acceptance older than this is not applied: the person may have walked away. */
const PENDING_TERMS_MAX_AGE_MS = 60 * 60 * 1000;

export interface AcceptanceMetadata extends TermsAcceptance {
  business_declared: true;
  language: SignupLanguage;
}

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem"> | null;

function local(): Store {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function acceptanceMetadata(
  language: SignupLanguage,
  now: Date = new Date()
): AcceptanceMetadata {
  return { ...termsAcceptance(now), business_declared: true, language };
}

export function rememberPendingTerms(language: SignupLanguage, store: Store = local()) {
  try {
    store?.setItem(PENDING_TERMS_KEY, JSON.stringify(acceptanceMetadata(language)));
  } catch {
    // blocked storage: the terms gate asks again after the redirect
  }
}

/** The pending acceptance, if fresh; removed either way. */
export function takePendingTerms(
  store: Store = local(),
  now: Date = new Date()
): AcceptanceMetadata | null {
  try {
    const raw = store?.getItem(PENDING_TERMS_KEY);
    store?.removeItem(PENDING_TERMS_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as AcceptanceMetadata;
    const at = Date.parse(value.terms_accepted_at);
    if (!value.terms_version || Number.isNaN(at)) return null;
    if (now.getTime() - at > PENDING_TERMS_MAX_AGE_MS) return null;
    return value;
  } catch {
    return null;
  }
}

export interface UserLike {
  user_metadata?: Record<string, unknown> | null;
  identities?: Array<{ provider: string }> | null;
}

/**
 * Whether this account still has to accept the terms: it was created through
 * Google or Microsoft (no e-mail identity) and never stored an acceptance.
 * E-mail accounts are never asked here: new ones accepted on the form, and
 * the ones from before the terms checkbox are customers on their contract.
 * An e-mail account that later signs in with Google keeps its e-mail identity
 * (Supabase links the two), so it is not asked either.
 */
export function needsTermsAcceptance(user: UserLike | null | undefined): boolean {
  if (!user) return false;
  if (user.user_metadata?.terms_version) return false;
  const identities = user.identities ?? [];
  if (identities.length === 0) return false;
  return !identities.some((i) => i.provider === "email");
}

/**
 * How an account that just accepted the terms in TermsGate was created, for the
 * `signed_up` event. TermsGate only ever asks new Google / Microsoft accounts, so
 * its first successful acceptance is that account's sign-up.
 */
export function oauthSignupMethod(user: UserLike | null | undefined): "google" | "azure" | "oauth" {
  const providers = (user?.identities ?? []).map((i) => i.provider);
  if (providers.includes("google")) return "google";
  if (providers.includes("azure")) return "azure";
  return "oauth";
}

/**
 * The providers switched on in Supabase, from `GET /auth/v1/settings`
 * (`{ external: { google: true, azure: false, … } }`). Anything unreadable
 * counts as off, so a button never leads to a provider that isn't set up.
 */
export function enabledProviders(settings: unknown): OAuthProvider[] {
  const external = (settings as { external?: Record<string, unknown> } | null)?.external;
  if (!external || typeof external !== "object") return [];
  return OAUTH_PROVIDERS.filter((provider) => external[provider] === true);
}
